import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import QuoteCreator from './components/QuoteCreator';
import QuotesView from './components/QuotesView';
import SettingsView from './components/SettingsView';
import CustomerTypeEditor from './components/CustomerTypeEditor';
import QuoteDetail from './components/QuoteDetail';
import UserProfile from './components/UserProfile';
import Auth from './components/Auth';
import Landing from './components/Landing';
import AboutPage from './components/AboutPage';
import PrivacyPage from './components/PrivacyPage';
import SignUpPage from './components/SignUpPage';
import OnboardingPage from './components/OnboardingPage';
import JobberIntegration from './components/JobberIntegration';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import { useSync, SyncProvider } from './contexts/SyncContext';
import { exchangeCodeForToken, parseJobberState } from './services/jobberConfig';
import { JobberService } from './services/jobberService';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-6">
              The app encountered an error. Please refresh the page to try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const WindowQuoteManager = () => {
  const { currentUser, logout } = useAuth();
  const { syncService } = useSync();
  
  // Add error handling for state initialization
  const [currentView, setCurrentView] = useState('quotes');
  const [quotes, setQuotes] = useState([]); // Quotes loaded from Firestore via real-time listener
  const [windowTypes, setWindowTypesState] = useState([]);
  const [customerTypes, setCustomerTypesState] = useState([]);
  const lastLocalWindowTypesUpdateRef = React.useRef(0);

  // Wrapper functions that also save to localStorage and sync to cloud
  const setWindowTypes = async (newWindowTypes) => {
    try {
      if (!Array.isArray(newWindowTypes)) {
        console.error('❌ [Browser] setWindowTypes received non-array');
        return;
      }
      
      setWindowTypesState(newWindowTypes);
      lastLocalWindowTypesUpdateRef.current = Date.now(); // Mark this as a local update
      
      if (currentUser) {
        localStorage.setItem(`windowTypes_${currentUser.uid}`, JSON.stringify(newWindowTypes));
        
        // Sync to cloud immediately
        try {
          await syncService.saveUserSettings({
            windowTypes: newWindowTypes,
            customerTypes: customerTypes,
            theme: isDarkMode ? 'dark' : 'light'
          }, currentUser.uid);
        } catch (error) {
          console.error('❌ [Browser] Error syncing window types:', error);
        }
      }
    } catch (error) {
      console.error('❌ [Browser] Error in setWindowTypes:', error);
    }
  };

  const setCustomerTypes = async (newCustomerTypes) => {
    setCustomerTypesState(newCustomerTypes);
    if (currentUser) {
      localStorage.setItem(`customerTypes_${currentUser.uid}`, JSON.stringify(newCustomerTypes));
      // Sync to cloud immediately
      try {
        await syncService.saveUserSettings({
          windowTypes: windowTypes,
          customerTypes: newCustomerTypes,
          theme: isDarkMode ? 'dark' : 'light'
        }, currentUser.uid);
      } catch (error) {
        console.error('❌ [Browser] Error syncing customer types:', error);
      }
    }
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [editingQuote, setEditingQuote] = useState(null);
  const [viewingQuote, setViewingQuote] = useState(null);
  const [editingCustomerType, setEditingCustomerType] = useState(null);
  const [settingsTab, setSettingsTab] = useState('windowTypes');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Clear localStorage for quotes on app load (one-time cleanup)
  // This removes any old quotes stored locally before we removed localStorage support
  useEffect(() => {
    if (currentUser) {
      try {
        const oldQuotes = localStorage.getItem(`quotes_${currentUser.uid}`);
        if (oldQuotes) {
          console.log('🧹 Cleaning up old quotes from localStorage');
          localStorage.removeItem(`quotes_${currentUser.uid}`);
          // Also clean up deleted quotes tracking
          localStorage.removeItem(`deletedQuotes_${currentUser.uid}`);
        }
      } catch (error) {
        console.warn('Error cleaning up localStorage:', error);
      }
    }
  }, [currentUser]);

  // Set up real-time sync listeners
  useEffect(() => {
    if (!currentUser) {
      setQuotes([]);
      // Default window types (will be replaced when user signs in and data loads)
      setWindowTypesState([
        { id: crypto.randomUUID(), name: 'Standard', price: 10, category: 'exterior_interior' },
        { id: crypto.randomUUID(), name: 'Double Sized', price: 15, category: 'exterior_interior' },
        { id: crypto.randomUUID(), name: 'French', price: 12, category: 'exterior_interior' },
        { id: crypto.randomUUID(), name: 'Screens', price: 5, category: 'separate' },
        { id: crypto.randomUUID(), name: 'Garage Door', price: 20, category: 'exterior_interior' }
      ]);
      setCustomerTypesState([
        { 
          id: crypto.randomUUID(), 
          name: 'Residential', 
          windowPrices: {
            'Standard': 10, 'Double Sized': 15, 'French': 12, 'Screens': 5, 'Garage Door': 20
          }
        },
        { 
          id: crypto.randomUUID(), 
          name: 'Commercial', 
          windowPrices: {
            'Standard': 12, 'Double Sized': 18, 'French': 14, 'Screens': 6, 'Garage Door': 24
          }
        }
      ]);
      return;
    }

    // Load Jobber sync queue on initialization
    syncService.loadJobberSyncQueue(currentUser.uid);
    
    // Process Jobber sync queue if Jobber is connected
    if (JobberService.isAuthenticated()) {
      syncService.processJobberSyncQueue();
    }
    
    // Track current Firestore snapshot to prevent saving quotes that don't exist
    let currentFirestoreQuoteIds = new Set();
    
    // Track quotes currently being migrated to prevent infinite loops
    const migratingQuotes = new Set();
    
    // Set up real-time quotes listener
    syncService.setupQuotesListener(currentUser.uid, (quotesFromFirestore) => {
      // Update tracked Firestore quote IDs (these are the ONLY quotes that exist in Firestore)
      currentFirestoreQuoteIds = new Set(quotesFromFirestore.map(q => q.id.toString()));
      
      // Check if any quotes actually need migration (compare actual data, not object references)
      const needsMigration = quotesFromFirestore.some(quote => {
        const quoteId = quote.id ? quote.id.toString() : null;
        // Skip if already migrating this quote
        if (quoteId && migratingQuotes.has(quoteId)) {
          return false;
        }
        return !quote.quoteNumber || quote.quoteNumber < 100;
      });
      
      if (needsMigration) {
        // Migrate quotes to have quote numbers if needed
        const migratedQuotes = migrateQuoteNumbers(quotesFromFirestore);
        
        // Only save quotes that actually changed (quoteNumber was added/changed)
        migratedQuotes.forEach(quote => {
          const original = quotesFromFirestore.find(q => q.id === quote.id);
          const quoteId = quote.id ? quote.id.toString() : null;
          
          // Only save if:
          // 1. Quote exists in Firestore (prevent saving deleted quotes)
          // 2. QuoteNumber actually changed (prevent infinite loop)
          // 3. Not already migrating this quote
          if (quoteId && currentFirestoreQuoteIds.has(quoteId) && original && !migratingQuotes.has(quoteId)) {
            const quoteNumberChanged = (original.quoteNumber || 0) !== (quote.quoteNumber || 0);
            if (quoteNumberChanged) {
              console.log(`📝 Migrating quote ${quoteId}: ${original.quoteNumber || 'none'} → ${quote.quoteNumber}`);
              // Mark as migrating to prevent re-migration
              migratingQuotes.add(quoteId);
              
              syncService.saveQuote(quote, currentUser.uid, true)
                .then(() => {
                  // Remove from migrating set after successful save
                  migratingQuotes.delete(quoteId);
                  console.log(`✅ Migration complete for quote ${quoteId}`);
                })
                .catch(err => {
                  // Remove from migrating set on error so it can retry
                  migratingQuotes.delete(quoteId);
                  console.warn('Could not save migrated quote:', err);
                });
            }
          } else {
            if (migratingQuotes.has(quoteId)) {
              console.log(`⏸️ Skipping migration save for quote ${quoteId} - already migrating`);
            } else {
              console.log(`⚠️ Skipping migration save for quote ${quoteId} - not in current Firestore snapshot`);
            }
          }
        });
        
        setQuotes(migratedQuotes);
      } else {
        // No migration needed, just use quotes from Firestore
        setQuotes(quotesFromFirestore);
      }
      
      // Also update editingQuote if it exists and ID changed
      if (editingQuote) {
        const currentQuotes = needsMigration ? migrateQuoteNumbers(quotesFromFirestore) : quotesFromFirestore;
        const updatedQuote = currentQuotes.find(q => q.id === editingQuote.id);
        if (updatedQuote && updatedQuote.id !== editingQuote.id) {
          setEditingQuote(updatedQuote);
        }
      }
    });

    // Set up real-time settings listener for users/{userId} document
    syncService.setupSettingsListener(currentUser.uid, (settings) => {
      const newWindowTypes = settings.windowTypes || [];
      const newCustomerTypes = settings.customerTypes || [];
      const newTheme = settings.theme || 'light';
      
      // Use functional updates to get latest state values (not stale closure values)
      setWindowTypesState(currentWindowTypes => {
        // Don't overwrite if we just made a local update (within last 3 seconds)
        const timeSinceLocalUpdate = Date.now() - lastLocalWindowTypesUpdateRef.current;
        const shouldIgnoreFirebaseUpdate = timeSinceLocalUpdate < 3000;
        
        // Only update if data actually changed to avoid infinite loops
        const windowTypesChanged = JSON.stringify(newWindowTypes) !== JSON.stringify(currentWindowTypes);
        
        if (windowTypesChanged) {
          if (shouldIgnoreFirebaseUpdate) {
            return currentWindowTypes; // Keep current state
          } else {
            // Also save to localStorage as backup
            localStorage.setItem(`windowTypes_${currentUser.uid}`, JSON.stringify(newWindowTypes));
            return newWindowTypes;
          }
        } else {
          return currentWindowTypes; // No change needed
        }
      });
      
      setCustomerTypesState(currentCustomerTypes => {
        // Only update if data actually changed to avoid infinite loops
        const customerTypesChanged = JSON.stringify(newCustomerTypes) !== JSON.stringify(currentCustomerTypes);
        
        if (customerTypesChanged) {
          // Also save to localStorage as backup
          localStorage.setItem(`customerTypes_${currentUser.uid}`, JSON.stringify(newCustomerTypes));
          return newCustomerTypes;
        } else {
          return currentCustomerTypes; // No change needed
        }
      });
      
      // Handle theme changes from Firebase
      if (newTheme) {
        const currentTheme = isDarkMode ? 'dark' : 'light';
        if (newTheme !== currentTheme) {
          setIsDarkMode(newTheme === 'dark');
          localStorage.setItem('windowQuoteManager_theme', newTheme);
          console.log('✅ Theme updated from Firebase:', newTheme);
        }
      }
    });

    // Cleanup listeners on unmount
    return () => {
      syncService.cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, syncService, editingQuote, isDarkMode]); // migrateQuoteNumbers is stable, intentionally omitted

  // Add useEffect for initialization and error handling
  useEffect(() => {
    try {
      // Simulate loading time for better UX
      const timer = setTimeout(() => setIsLoading(false), 500);
      return () => clearTimeout(timer);
    } catch (error) {
      console.error('Initialization error:', error);
      setIsLoading(false);
    }
  }, []);

  // Load theme preference from localStorage and Firebase
  useEffect(() => {
    const loadThemePreference = async () => {
      try {
        // First, try to load from localStorage (fast, immediate)
        const savedTheme = localStorage.getItem('windowQuoteManager_theme');
        if (savedTheme) {
          setIsDarkMode(savedTheme === 'dark');
        }
        
        // If user is logged in, also check Firebase (may override localStorage)
        if (currentUser) {
          try {
            const userSettings = await syncService.getUserSettings(currentUser.uid);
            if (userSettings.theme && userSettings.theme !== savedTheme) {
              // Firebase has a different theme, use it
              setIsDarkMode(userSettings.theme === 'dark');
              localStorage.setItem('windowQuoteManager_theme', userSettings.theme);
              console.log('✅ Theme loaded from Firebase:', userSettings.theme);
            }
          } catch (firebaseError) {
            console.warn('Could not load theme from Firebase:', firebaseError);
            // Continue with localStorage value
          }
        }
      } catch (error) {
        console.warn('Could not load theme preference:', error);
      }
    };
    
    loadThemePreference();
  }, [currentUser, syncService]);

  // Apply theme to DOM
  useEffect(() => {
    const htmlElement = document.documentElement;
    if (isDarkMode) {
      htmlElement.setAttribute('data-theme', 'dark');
    } else {
      htmlElement.setAttribute('data-theme', 'light');
    }
  }, [isDarkMode]);

  // Document title per app view
  useEffect(() => {
    const titles = {
      quotes: 'Squeekit - Quotes',
      newQuote: 'Squeekit - New Quote',
      editingQuote: 'Squeekit - Edit Quote',
      viewingQuote: viewingQuote ? `Squeekit - Quote #${viewingQuote.quoteNumber || 100}` : 'Squeekit - Quote',
      settings: 'Squeekit - Settings',
      userProfile: 'Squeekit - Profile',
      editingCustomerType: 'Squeekit - Customer Type',
    };
    document.title = titles[currentView] || 'Squeekit - Quotes';
  }, [currentView, viewingQuote]);

  // Save theme preference
  const handleThemeToggle = async (newTheme) => {
    try {
      const themeValue = newTheme ? 'dark' : 'light';
      setIsDarkMode(newTheme);
      
      // Always save to localStorage for immediate persistence
      localStorage.setItem('windowQuoteManager_theme', themeValue);
      
      // If user is logged in, also save to Firebase for cross-device sync
      if (currentUser) {
        try {
          await syncService.saveUserSettings({
            windowTypes: windowTypes,
            customerTypes: customerTypes,
            theme: themeValue
          }, currentUser.uid);
          console.log('✅ Theme preference saved to Firebase');
        } catch (firebaseError) {
          console.warn('Could not save theme to Firebase:', firebaseError);
          // Continue anyway - localStorage is saved
        }
      }
    } catch (error) {
      console.warn('Could not save theme preference:', error);
      setIsDarkMode(newTheme);
    }
  };


  // Design system now uses CSS custom properties
  // No theme object needed - all styling handled by CSS classes

  // Get next sequential quote number (starting at 100)
  const getNextQuoteNumber = () => {
    if (!quotes || quotes.length === 0) {
      return 100;
    }
    
    // Find the maximum quote number, defaulting to 99 if none found
    const maxQuoteNumber = quotes.reduce((max, quote) => {
      const quoteNum = quote.quoteNumber || 0;
      return quoteNum > max ? quoteNum : max;
    }, 99);
    
    return maxQuoteNumber + 1;
  };

  // Migrate existing quotes to have quote numbers (starting at 100)
  const migrateQuoteNumbers = (quotesList) => {
    if (!quotesList || quotesList.length === 0) {
      return quotesList;
    }
    
    // Filter quotes that need migration (no quoteNumber or invalid number)
    const quotesNeedingMigration = quotesList.filter(q => !q.quoteNumber || q.quoteNumber < 100);
    
    if (quotesNeedingMigration.length === 0) {
      return quotesList; // No migration needed
    }
    
    // Find the maximum existing quote number
    const maxExistingNumber = quotesList.reduce((max, quote) => {
      const quoteNum = quote.quoteNumber || 0;
      return quoteNum > max ? quoteNum : max;
    }, 99);
    
    // Assign sequential numbers starting from max + 1, or 100 if no valid numbers exist
    let nextNumber = maxExistingNumber >= 100 ? maxExistingNumber + 1 : 100;
    
    const migratedQuotes = quotesList.map(quote => {
      if (!quote.quoteNumber || quote.quoteNumber < 100) {
        return { ...quote, quoteNumber: nextNumber++ };
      }
      return quote;
    });
    
    console.log(`📝 Migrated ${quotesNeedingMigration.length} quotes with quote numbers`);
    
    return migratedQuotes;
  };

  const handleSaveQuote = async (quote) => {
    console.log('🔵 handleSaveQuote called with quote:', quote);
    
    if (!currentUser) {
      console.error('❌ No current user');
      alert('Please sign in to save quotes.');
      return;
    }

    try {
      // Check if quote has a Firestore ID (not temp) to determine if it's an update
      const isUpdate = !!editingQuote && quote.id && 
                       typeof quote.id === 'string' && 
                       !quote.id.startsWith('temp_');
      console.log('🔍 Is update?', isUpdate);
      
      // Assign quote number for new quotes (not updates)
      let quoteWithNumber = { ...quote };
      if (!isUpdate && (!quote.quoteNumber || quote.quoteNumber < 100)) {
        quoteWithNumber.quoteNumber = getNextQuoteNumber();
        console.log('📝 Assigned quote number:', quoteWithNumber.quoteNumber);
      }
      
      // Add userId to quote
      const quoteWithUser = {
        ...quoteWithNumber,
        userId: currentUser.uid
      };

      console.log('💾 Saving quote...', quoteWithUser);
      
      // Save quote directly to Firestore
      // Real-time listener will automatically update state
      const savedQuote = await syncService.saveQuote(quoteWithUser, currentUser.uid, isUpdate);
      
      console.log('✅ Quote saved to Firestore', savedQuote);
      
      if (editingQuote) {
        setEditingQuote(null);
      }
      
      setCurrentView('quotes');
      
      // Return the saved quote so QuoteCreator can use it for Jobber sync
      return savedQuote;
    } catch (error) {
      console.error('❌ Error saving quote:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert('Error saving quote: ' + (error.message || 'Please try again.'));
    }
  };

  const handleSaveCustomerType = async (customerType) => {
    // Convert string prices to numbers when saving
    const finalCustomerType = {
      ...customerType,
      windowPrices: Object.fromEntries(
        Object.entries(customerType.windowPrices).map(([key, value]) => [
          key, 
          value === '' ? 0 : parseFloat(value) || 0
        ])
      )
    };
    
    const updatedCustomerTypes = customerTypes.map(c => c.id === finalCustomerType.id ? finalCustomerType : c);
    
    // Update state (this will trigger setCustomerTypes which syncs to Firebase)
    await setCustomerTypes(updatedCustomerTypes);
    
    setEditingCustomerType(null);
    setSettingsTab('customerTypes');
    setCurrentView('settings');
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!currentUser) {
      alert('Please sign in to delete quotes.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this quote? This action cannot be undone.')) {
      return;
    }

    try {
      // Remove from Jobber sync queue if queued
      syncService.removeFromJobberSyncQueue(quoteId, currentUser.uid);
      
      // Delete quote directly from Firestore
      // Real-time listener will automatically update state
      await syncService.deleteQuote(quoteId, currentUser.uid);
      
      console.log('✅ Quote deleted from Firestore');
    } catch (error) {
      console.error('Error deleting quote:', error);
      
      // Show user-friendly error message
      if (error.code === 'permission-denied') {
        alert('Error: You do not have permission to delete this quote. Please make sure you are signed in and this is your quote.');
      } else if (error.code === 'not-found') {
        // Quote already deleted, that's fine
        console.log('Quote already deleted');
      } else {
        alert(`Error deleting quote: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const handleCreateSimilarQuote = (originalQuote) => {
    // Create a new quote with the same line items but zeroed out counts and customer info
    const similarQuote = {
      id: Date.now(),
      customer: {
        name: '',
        address: '',
        phone: '',
        email: '',
        type: originalQuote.customer.type // Keep the same customer type
      },
      lineItems: originalQuote.lineItems.map(item => ({
        ...item,
        id: Date.now() + Math.random(), // New ID
        counts: '', // Zero out counts
        totalCount: 0, // Zero out total count
        price: 0 // Zero out price
      })),
      settings: { ...originalQuote.settings }, // Keep the same settings
      totals: {
        exteriorTotal: 0,
        interiorTotal: 0,
        separateItems: [],
        total: 0
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    setEditingQuote(similarQuote);
    setCurrentView('editingQuote');
  };

  // Show loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-accent-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-primary">Loading Window Quote Manager...</h2>
          <p className="text-tertiary mt-2">Please wait while we set up your workspace</p>
        </div>
      </div>
    );
  }

  if (currentView === 'newQuote') {
    return (
      <div className="mobile-app">
        <QuoteCreator 
          onSave={handleSaveQuote} 
          onCancel={() => setCurrentView('quotes')} 
          windowTypes={windowTypes}
          customerTypes={customerTypes}
          isDarkMode={isDarkMode}
        />
      </div>
    );
  }

  if (currentView === 'editingQuote') {
    return (
      <div className="mobile-app">
        <QuoteCreator 
          quote={editingQuote} 
          onSave={handleSaveQuote} 
          onCancel={() => setCurrentView('quotes')} 
          windowTypes={windowTypes}
          customerTypes={customerTypes}
          isDarkMode={isDarkMode}
        />
      </div>
    );
  }

  const handleDeleteCustomerType = (id) => {
    setCustomerTypes(customerTypes.filter(type => type.id !== id));
    setSettingsTab('customerTypes');
    setCurrentView('settings');
  };

  if (currentView === 'editingCustomerType') {
    return (
      <div className="mobile-app">
        <CustomerTypeEditor 
          customerType={editingCustomerType} 
          onSave={handleSaveCustomerType} 
          onCancel={() => {
            setSettingsTab('customerTypes');
            setCurrentView('settings');
          }} 
          windowTypes={windowTypes}
          isDarkMode={isDarkMode}
          onDelete={handleDeleteCustomerType}
        />
      </div>
    );
  }

  if (currentView === 'viewingQuote') {
    // Use quote.quoteNumber if available, otherwise default to 100
    const quoteNumber = viewingQuote.quoteNumber || 100;
    
    return (
      <div className="mobile-app">
        <QuoteDetail 
          quote={viewingQuote}
          quoteNumber={quoteNumber}
          windowTypes={windowTypes}
          currentUser={currentUser}
          onEdit={(quote) => {
            setEditingQuote(quote);
            setCurrentView('editingQuote');
          }}
          onBack={() => setCurrentView('quotes')}
          onCreateSimilar={handleCreateSimilarQuote}
          onDelete={(quoteId) => {
            handleDeleteQuote(quoteId);
            setCurrentView('quotes');
          }}
        />
      </div>
    );
  }

  if (currentView === 'settings') {
    return (
      <div className="mobile-app">
        <SettingsView 
          windowTypes={windowTypes}
          setWindowTypes={setWindowTypes}
          customerTypes={customerTypes}
          setCustomerTypes={setCustomerTypes}
          setCurrentView={setCurrentView}
          setEditingCustomerType={setEditingCustomerType}
          settingsTab={settingsTab}
          setSettingsTab={setSettingsTab}
          isDarkMode={isDarkMode}
          handleThemeToggle={handleThemeToggle}
        />
      </div>
    );
  }

  if (currentView === 'userProfile') {
    return (
      <div className="mobile-app">
        <UserProfile 
          onBack={() => setCurrentView('quotes')}
          logout={logout}
        />
      </div>
    );
  }

  return (
    <div className="mobile-app">
      <QuotesView 
        quotes={quotes}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        setCurrentView={setCurrentView}
        setEditingQuote={setEditingQuote}
        setViewingQuote={setViewingQuote}
        handleDeleteQuote={handleDeleteQuote}
        isDarkMode={isDarkMode}
        handleThemeToggle={handleThemeToggle}
        currentUser={currentUser}
      />
    </div>
  );
};

// Add Jobber integration modal
const JobberIntegrationModal = ({ show, onClose, currentUser }) => {
  if (!show) return null;

  return (
    <JobberIntegration
      onClose={onClose}
      currentUser={currentUser}
    />
  );
};

const AppContent = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [showJobberIntegration, setShowJobberIntegration] = React.useState(false);

  React.useEffect(() => {
    if (currentUser) {
      JobberService.loadTokensForUser(currentUser.uid);
    } else {
      JobberService.clearTokenCache();
    }
  }, [currentUser]);

  React.useEffect(() => {
    const handleOpenJobberIntegration = () => {
      setShowJobberIntegration(true);
    };
    window.addEventListener('openJobberIntegration', handleOpenJobberIntegration);

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state) {
      const { userId } = parseJobberState(state);
      exchangeCodeForToken(code).then(async (tokenData) => {
        if (userId) {
          await JobberService.storeTokens(tokenData, userId);
        }
        if (window.opener) {
          window.opener.postMessage({
            type: 'JOBBER_OAUTH_SUCCESS',
            data: tokenData
          }, window.location.origin);
        }
        window.close();
      }).catch(error => {
        console.error('OAuth callback error:', error);
        if (window.opener) {
          window.opener.postMessage({
            type: 'JOBBER_OAUTH_ERROR',
            error: error.message
          }, window.location.origin);
        }
        window.close();
      });
    }

    return () => {
      window.removeEventListener('openJobberIntegration', handleOpenJobberIntegration);
    };
  }, []);

  if (location.pathname === '/onboarding') {
    return <OnboardingPage />;
  }

  if (currentUser) {
    return (
      <>
        <WindowQuoteManager />
        <JobberIntegrationModal
          show={showJobberIntegration}
          onClose={() => setShowJobberIntegration(false)}
          currentUser={currentUser}
        />
      </>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/signin" element={<Auth />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SyncProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </SyncProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;